package com.milta.feature.route.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.route.domain.usecase.GetRouteHistoryUseCase
import com.milta.feature.route.domain.usecase.GetRouteProofsUseCase
import com.milta.feature.team.domain.usecase.GetEnterpriseDriversUseCase
import com.milta.feature.team.domain.usecase.GetMyTeamUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.todayIn
import kotlin.time.Clock

private const val HISTORY_PAGE_SIZE = 20
private const val PROOFS_PAGE_SIZE = 20

/** MIL-86 — same debounce delay as [DeliveryEntryViewModel]'s address-autocomplete search, the
 * only other free-text-search-while-typing precedent in this codebase. */
private const val SEARCH_DEBOUNCE_MS = 400L

/** Backend rejects any `from` older than `today-7` (MIL-52 contract) — mirrored client-side as
 * [minSelectableHistoryDateIso], the date pickers' `min` bound, so the user can never even pick an
 * invalid date (belt-and-suspenders with the server-side 400 `error.invalid_date`, see
 * [mapHistoryError] below). */
private const val MAX_PAST_DAYS = 7

/** Today, `yyyy-MM-dd` — same `kotlinx-datetime` mechanism already used for date toggles elsewhere
 * (see [WebDashboardViewModel.tomorrowIso]); also the date pickers' `max` bound. Public (not
 * `private`/`internal`) because the webApp screen needs the exact same bound to configure the
 * native `<input type="date">` `min`/`max` attributes. */
fun todayIso(): String = Clock.System.todayIn(TimeZone.currentSystemDefault()).toString()

/** MIL-52 — the oldest date the "Date précise"/"Plage de dates" pickers may select, `yyyy-MM-dd`. */
fun minSelectableHistoryDateIso(): String =
    Clock.System.todayIn(TimeZone.currentSystemDefault()).minus(MAX_PAST_DAYS, DateTimeUnit.DAY).toString()

/**
 * ViewModel for MIL-52 "Historique des preuves de livraison" — owns both screens of the flow (see
 * [WebHistoriqueState] doc for why one ViewModel covers both). Reuses [GetMyTeamUseCase]/
 * [GetEnterpriseDriversUseCase] to resolve the enterprise's driver list for the "Livreur" filter
 * dropdown exactly like [com.milta.feature.team.presentation.WebTeamViewModel]/
 * [WebImportRouteViewModel.loadDrivers] already do — no new driver-list endpoint introduced here.
 */
class WebHistoriqueViewModel(
    private val getRouteHistory: GetRouteHistoryUseCase,
    private val getRouteProofs: GetRouteProofsUseCase,
    private val getMyTeam: GetMyTeamUseCase,
    private val getEnterpriseDrivers: GetEnterpriseDriversUseCase,
    // Runs the ViewModel's coroutines (see MviViewModel): tests pass the runTest scheduler's dispatcher.
    dispatcher: CoroutineDispatcher = Dispatchers.Default,
) : MviViewModel<WebHistoriqueState, WebHistoriqueIntent>(WebHistoriqueState(), dispatcher) {

    // MIL-86 — one cancelable Job per search field, same cancel-then-relaunch debounce pattern as
    // DeliveryEntryViewModel.autocompleteJob. Plain ViewModel fields, not state: a pending debounce
    // isn't something the UI renders.
    private var historySearchJob: Job? = null
    private var proofsSearchJob: Job? = null

    init {
        onIntent(WebHistoriqueIntent.Load)
    }

    override fun onIntent(intent: WebHistoriqueIntent) {
        when (intent) {
            WebHistoriqueIntent.Load -> {
                loadDrivers()
                loadHistory()
            }
            is WebHistoriqueIntent.SelectPeriodMode -> selectPeriodMode(intent.mode)
            is WebHistoriqueIntent.SelectSingleDate -> {
                setState { copy(singleDate = intent.date, historyPageNumber = 1) }
                loadHistory()
            }
            is WebHistoriqueIntent.SelectRangeFrom -> {
                setState { copy(rangeFrom = intent.date, historyPageNumber = 1) }
                loadHistory()
            }
            is WebHistoriqueIntent.SelectRangeTo -> {
                setState { copy(rangeTo = intent.date, historyPageNumber = 1) }
                loadHistory()
            }
            is WebHistoriqueIntent.SelectDriver -> {
                setState { copy(selectedDriverId = intent.driverId, historyPageNumber = 1) }
                loadHistory()
            }
            is WebHistoriqueIntent.GoToHistoryPage -> {
                setState { copy(historyPageNumber = intent.page) }
                loadHistory()
            }
            is WebHistoriqueIntent.OpenRouteProofs -> openRouteProofs(intent.routeId)
            WebHistoriqueIntent.BackToHistory -> setState {
                copy(selectedRouteId = null, proofsResult = null, proofsPageNumber = 1, proofsError = null, proofsSearchQuery = "")
            }
            is WebHistoriqueIntent.GoToProofsPage -> {
                setState { copy(proofsPageNumber = intent.page) }
                loadProofs()
            }
            is WebHistoriqueIntent.SetHistorySearchQuery -> debounceHistorySearch(intent.query)
            is WebHistoriqueIntent.SetProofsSearchQuery -> debounceProofsSearch(intent.query)
        }
    }

    /** MIL-86 — updates the field immediately (so the input reflects every keystroke) but delays
     * the actual re-fetch, cancelling any still-pending one from a previous keystroke. Always
     * resets to page 1: a search narrows the result set, the current page number may no longer
     * exist. */
    private fun debounceHistorySearch(query: String) {
        setState { copy(historySearchQuery = query, historyPageNumber = 1) }
        historySearchJob?.cancel()
        historySearchJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MS)
            loadHistory()
        }
    }

    private fun debounceProofsSearch(query: String) {
        setState { copy(proofsSearchQuery = query, proofsPageNumber = 1) }
        proofsSearchJob?.cancel()
        proofsSearchJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MS)
            loadProofs()
        }
    }

    /** Resets to a sane default date for the newly-selected mode rather than keeping a stale value
     * typed under a previous mode — mirrors [WebDashboardViewModel.switchTab]'s reset-then-reload. */
    private fun selectPeriodMode(mode: HistoriquePeriodMode) {
        if (mode == currentState.periodMode) return
        val today = todayIso()
        setState {
            copy(
                periodMode = mode,
                singleDate = if (mode == HistoriquePeriodMode.SingleDate) (singleDate ?: today) else singleDate,
                rangeFrom = if (mode == HistoriquePeriodMode.Range) (rangeFrom ?: minSelectableHistoryDateIso()) else rangeFrom,
                rangeTo = if (mode == HistoriquePeriodMode.Range) (rangeTo ?: today) else rangeTo,
                historyPageNumber = 1,
            )
        }
        loadHistory()
    }

    /** Mirrors [com.milta.feature.team.presentation.WebTeamViewModel.loadTeam] — a failure to
     * resolve the enterpriseId or fetch the drivers is silent (empty list, dropdown just shows
     * "Tous les livreurs" alone) rather than a dedicated error banner on top of the history list. */
    private fun loadDrivers() {
        scope.launch {
            val enterpriseId = getMyTeam().getOrNull()?.firstOrNull()?.enterpriseId ?: return@launch
            getEnterpriseDrivers(enterpriseId).onSuccess { drivers -> setState { copy(drivers = drivers) } }
        }
    }

    private fun loadHistory() {
        val (from, to) = periodBounds()
        val driverId = currentState.selectedDriverId
        val search = currentState.historySearchQuery.trim().ifBlank { null }
        val page = currentState.historyPageNumber
        scope.launch {
            setState { copy(isLoadingHistory = true, historyError = null) }
            runCatching { getRouteHistory(from, to, driverId, search, page, HISTORY_PAGE_SIZE) }.fold(
                onSuccess = { result -> setState { copy(isLoadingHistory = false, historyPage = result) } },
                onFailure = { e -> setState { copy(isLoadingHistory = false, historyError = mapHistoryError(e)) } },
            )
        }
    }

    private fun openRouteProofs(routeId: String) {
        setState { copy(selectedRouteId = routeId, proofsResult = null, proofsPageNumber = 1, proofsError = null, proofsSearchQuery = "") }
        loadProofs()
    }

    private fun loadProofs() {
        val routeId = currentState.selectedRouteId ?: return
        val search = currentState.proofsSearchQuery.trim().ifBlank { null }
        val page = currentState.proofsPageNumber
        scope.launch {
            setState { copy(isLoadingProofs = true, proofsError = null) }
            runCatching { getRouteProofs(routeId, search, page, PROOFS_PAGE_SIZE) }.fold(
                onSuccess = { result -> setState { copy(isLoadingProofs = false, proofsResult = result) } },
                onFailure = { e -> setState { copy(isLoadingProofs = false, proofsError = mapProofsError(e)) } },
            )
        }
    }

    /** `null, null` for [HistoriquePeriodMode.LastEightDays] (server defaults to the rolling last 8
     * days), otherwise the explicit bounds already clamped client-side by the date pickers'
     * `min`/`max` (see [minSelectableHistoryDateIso]/[todayIso]). */
    private fun periodBounds(): Pair<String?, String?> = when (currentState.periodMode) {
        HistoriquePeriodMode.LastEightDays -> null to null
        HistoriquePeriodMode.SingleDate -> currentState.singleDate to currentState.singleDate
        HistoriquePeriodMode.Range -> currentState.rangeFrom to currentState.rangeTo
    }

    private fun mapHistoryError(e: Throwable): String = when {
        // Backend maps ValidationException(code="invalid_date") to 400, not 422 — same convention
        // as every other date-validation error in RoutesController (GlobalExceptionHandler.cs:46).
        e is ApiException && e.statusCode == 400 -> "invalid_period"
        e is ApiException -> "server_error"
        else -> "offline"
    }

    /** Mirrors [WebDashboardViewModel.mapRouteDetailError]'s codes exactly. */
    private fun mapProofsError(e: Throwable): String = when {
        e is ApiException && e.statusCode == 404 -> "route_not_found"
        e is ApiException && e.statusCode == 403 -> "forbidden"
        e !is ApiException -> "offline"
        else -> "server_error"
    }
}
